import React from 'react';
import { createRoot } from 'react-dom/client';
import { Position, OverlayToaster, ToastProps } from '@blueprintjs/core';

const containerElement = document.createElement('div');
document.body.appendChild(containerElement);

let toasterRef: OverlayToaster | null = null;

createRoot(containerElement).render(
  <OverlayToaster
    position={Position.TOP}
    usePortal={false}
    ref={(ref) => {
      toasterRef = ref;
    }}
  />,
);

/**
 * Dérive une clé stable à partir du contenu du toast, afin que Blueprint
 * remplace le toast existant au lieu d'en empiler un second identique.
 * Motif : un même échec est souvent notifié par deux couches (intercepteur
 * axios global + `catch` local du composant) ; sans clé, l'utilisateur voit
 * le message deux fois.
 * Seuls les messages « sérialisables » (chaîne) sont dédupliqués ; un noeud
 * React est laissé tel quel (comportement historique).
 */
const deriveToastKey = (props: ToastProps): string | undefined => {
  const { message, intent } = props;
  const text =
    typeof message === 'string'
      ? message
      : typeof message === 'number'
        ? String(message)
        : null;

  if (!text) {
    return undefined;
  }
  // Un hash court suffit : la clé sert uniquement à l'égalité, pas à la lecture.
  let hash = 0;
  const raw = `${intent ?? 'none'}::${text}`;

  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 31 + raw.charCodeAt(i)) | 0;
  }
  return `app-toast-${hash >>> 0}`;
};

/**
 * Blueprint remplace un toast existant lorsqu'on lui repasse la même clé, mais
 * il compare sur son `state`, qui n'est pas encore commité quand deux couches
 * notifient la même erreur dans le même tick (auto-batching React 18). On
 * garde donc une trace des clés récemment affichées et on ignore purement le
 * doublon rapproché.
 */
const SAME_TICK_WINDOW_MS = 500;
const lastShownAt = new Map<string, number>();

export const AppToaster = {
  show: (props: ToastProps, key?: string) => {
    const toastKey = key ?? deriveToastKey(props);

    if (toastKey) {
      const now = Date.now();
      const previous = lastShownAt.get(toastKey);

      lastShownAt.set(toastKey, now);

      // Purge des entrées expirées pour éviter une croissance sans fin.
      if (lastShownAt.size > 50) {
        lastShownAt.forEach((at, k) => {
          if (now - at > SAME_TICK_WINDOW_MS) {
            lastShownAt.delete(k);
          }
        });
      }

      if (previous !== undefined && now - previous < SAME_TICK_WINDOW_MS) {
        // Doublon immédiat : le toast est déjà à l'écran, on ne l'empile pas.
        return toastKey;
      }
    }
    return toasterRef?.show(props, toastKey) ?? toastKey ?? '';
  },
  dismiss: (key: string) => toasterRef?.dismiss(key),
  clear: () => {
    lastShownAt.clear();
    toasterRef?.clear();
  },
  getToasts: () => toasterRef?.getToasts() ?? [],
};
